// Core packages
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { Router } from "@angular/router";

// Third party packages
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import moment from "moment";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { distinctUntilChanged, finalize, startWith } from "rxjs/operators";

// Custom packages - Models
import { AuthService } from "src/app/modules/auth/auth.service";
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { PricesService } from "src/app/modules/prices/prices.service";
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { SubscriptionsService } from "src/app/modules/subscriptions/subscriptions.service";
import { ICompany } from "src/app/shared/models/company/company.interface";
import { IPrice } from "src/app/shared/models/price/price.interface";
import { Property } from "src/app/shared/models/property/property.model";
import { IArchediSubscription } from "src/app/shared/models/subscription/subscription.interface";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ThemeService } from "src/app/shared/services/theme.service";

@Component({
  selector: "app-add-property",
  templateUrl: "./add-property.page.html",
  styleUrls: ["./add-property.page.scss"],
})
export class AddPropertyComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Add property form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  formSC: FormGroup = new FormGroup({});
  model: any = {};
  modelSC: any = {};
  anagraphicFields: FormlyFieldConfig[] = [];
  contractFields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  // END - Add property form
  archediSubscriptions: IArchediSubscription[] = [];
  defaultFee = 0;
  defaultAvailableSpace = 0;
  defaultExtraSpaceFee = 0;
  defaultStructurePrice = 0;
  defaultNfcAccessFee = 0;
  defaultSuppliersFee = 0;
  masterStarDateSupplier: Date;
  masterEndDateSupplier: Date;

  prices: IPrice[] = [];
  availableSubscriptions: any[];
  availableSubscriptionsKeys: any[];

  defaultStartDate: string;
  defaultEndDate: string;

  detailCardIsLoading = true;
  contractCardIsLoading = true;

  previousValue: any = {};
  previousSafetyContract: any = {};
  previousAntifireContract: any = {};
  previousInvoiceHolder: any;
  previousKey: any;
  previousFlatRate: any;
  previousFlatRateType: any;

  companyExists = false;

  // List of codes
  firstScanCodes = this.configService.availableScanCodes;
  firstFixedPackCodes = this.configService.availableFixedPackageCodes;
  scanInsNextCodesForfait = this.configService.availableInsCodesNextForfait;
  scanNextCodesForfait = this.configService.availableScanCodesNextForfait;

  /**
   * Class constructor
   */
  constructor(
    private authService: AuthService,
    public helperService: HelperService,
    public configService: ConfigService,
    private themeService: ThemeService,
    private cookieService: CookieService,
    private translateService: TranslateService,
    private propertiesService: PropertiesService,
    private companiesService: CompaniesService,
    private router: Router,
    private toastrService: ToastrService,
    private apiService: ApiService,
    private subscriptionsService: SubscriptionsService,
    private pricesService: PricesService
  ) {}

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(
      this.translateService.instant("properties.add.pageTitle")
    );

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Se default start and end date
    this.defaultStartDate = moment().format("YYYY-MM-DD"); //.toISOString();
    this.defaultEndDate = moment().add(1, "years").subtract(1, 'days').format("YYYY-MM-DD"); //.toISOString();

    this.getPrices();
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
   ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Get prices from backend
   *
   * @since 1.0.0
   */
  getPrices(): void {
    this.subscriptions.push(
      this.pricesService.getList(0, 15).subscribe((res) => {
        if (res.status) {
          this.prices.push(...res.data.items);
          this.getSubscriptionsAndInitForm();
        }
      })
    );
  }

  /**
   * Load requested subscription data
   *
   * @since 1.0.0
   */
  getSubscriptionsAndInitForm(): void {
    this.subscriptionsService.getList().subscribe(
      (res) => {
        if (res.status) {
          this.archediSubscriptions = res.data.items;
          // Init default value from FULL subscription
          const index = this.archediSubscriptions.findIndex((el: any) => {
            return el.key === "FULL";
          });
          if (index > -1) {
            this.defaultFee = this.archediSubscriptions[index].fee;
            this.defaultAvailableSpace = this.archediSubscriptions[
              index
            ].availableSpace;
            this.defaultExtraSpaceFee = this.archediSubscriptions[
              index
            ].extraSpaceFee;
            this.defaultStructurePrice = this.archediSubscriptions[
              index
            ].structurePrice;
            this.defaultNfcAccessFee = this.archediSubscriptions[
              index
            ].nfcAccessFee;
            this.defaultSuppliersFee = this.archediSubscriptions[
              index
            ].suppliersFee;
          }

          const subscriptions = res.data.items.map((item: any) => {
            return {
              value: item,
              label: item.name,
            };
          });
          this.availableSubscriptions = subscriptions.filter((element: any) => {
            return (
              element.value.key !== "QRANTINCENDIO"
            );
          });

          if (!this.availableSubscriptions.length) {
            console.warn("No available subscriptions!");
          }

          this.availableSubscriptionsKeys = this.availableSubscriptions.map(
            (element: any) => {
              return {
                value: element.value.key,
                label: element.value.name,
              };
            }
          );

          // Create an empty value
          const noSubscription = {
            value: "noSubscription",
            label: this.translateService.instant(
              "subscriptions.fields.noSubscription"
            ),
          };
          this.availableSubscriptionsKeys.push(noSubscription);

          // Init add form
          this.initForm();

          this.detailCardIsLoading = false;
          this.contractCardIsLoading = false;
        }
      },
      (err) => {
        this.helperService.handleError(err);
      }
    );
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Set model
    this.model = new Property();
    this.modelSC = new Property();

    // For price list sorting
    let index = 0;

    // Set fields
    this.anagraphicFields = [
      // ...this.anagraphicFields,
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12 col-md-6",
            type: "autocomplete",
            key: "admin.company",
            templateOptions: {
              customLabel: this.translateService.instant(
                "properties.fields.admin"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.admin"
              ),
              required: true,
              bindLabel: "name",
              bindValue: "_id",
              multiple: false,
              search$: this.companiesService.getListAdmin.bind(this),
              initialValues: [],
              hideSelected: true,
              typeToSearchText: this.translateService.instant(
                "properties.fields.insert_admin"
              ),
              clearAllText: this.translateService.instant(
                "common.words.clearAll"
              ),
              notFoundText: this.translateService.instant(
                "common.words.notFound"
              ),
              loadingText: this.translateService.instant(
                "common.words.loading"
              ),
              callback: (data: ICompany): void => {
                this.onAdminCompanySelected(data);
              },
            },
          },
          {
            className: "col-12 col-md-6",
            type: "autocomplete",
            key: "owner",
            templateOptions: {
              customLabel: this.translateService.instant(
                "properties.fields.owner"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.owner"
              ),
              required: false,
              bindLabel: "name",
              bindValue: "_id",
              multiple: false,
              search$: this.companiesService.getListOwner.bind(this),
              initialValues: [],
              hideSelected: true,
              typeToSearchText: this.translateService.instant(
                "properties.fields.insert_owner"
              ),
              clearAllText: this.translateService.instant(
                "common.words.clearAll"
              ),
              notFoundText: this.translateService.instant(
                "common.words.notFound"
              ),
              loadingText: this.translateService.instant(
                "common.words.loading"
              ),
            },
          },
          {
            className: "col-12 col-md-4 col-xl-3",
            type: "select",
            key: "type",
            templateOptions: {
              label: this.translateService.instant("properties.fields.type"),
              placeholder: this.translateService.instant(
                "properties.fields.type"
              ),
              required: true,
              options: [
                {
                  value: "co",
                  label: this.translateService.instant("properties.types.co"),
                },
                {
                  value: "bsc",
                  label: this.translateService.instant("properties.types.bsc"),
                },
                {
                  value: "pur",
                  label: this.translateService.instant("properties.types.pur"),
                },
                {
                  value: "puc",
                  label: this.translateService.instant("properties.types.puc"),
                },
              ],
            },
          },
          {
            className: "col-12 col-md-4 col-xl-3",
            type: "input",
            key: "name",
            templateOptions: {
              label: this.translateService.instant("properties.fields.name"),
              placeholder: this.translateService.instant(
                "properties.fields.name"
              ),
              required: true,
              minLength: 2,
              maxLength: 100,
            },
          },
          {
            className: "col-12 col-md-4 col-xl-3",
            type: "input",
            key: "notifyMail1",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.notifyMail1"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.notifyMail1"
              ),
              required: true,
              minLength: 5,
              maxLength: 320,
              type: "email",
            },
          },
          {
            className: "col-12 col-md-4 col-xl-3",
            type: "input",
            key: "notifyMail2",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.notifyMail2"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.notifyMail2"
              ),
              required: false,
              minLength: 5,
              maxLength: 320,
              type: "email",
            },
          },
          {
            className: "col-12 col-md-4 col-xl-3",
            type: "input",
            key: "masterCode",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.masterCode"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.masterCode"
              ),
              required: false,
              minLength: 0,
              maxLength: 50,
            },
          },
          {
            className: "col-12 col-md-9",
            type: "address",
            key: "address",
            templateOptions: {
              label: this.translateService.instant("properties.fields.address"),
              placeholder: this.translateService.instant(
                "properties.fields.address"
              ),
              required: true,
            },
          },
          {
            className: "col-12 col-sm-3",
            type: "input",
            key: "taxCode",
            templateOptions: {
              label: this.translateService.instant("properties.fields.taxCode"),
              placeholder: this.translateService.instant(
                "properties.fields.taxCode"
              ),
              required: true,
              minLength: 0,
              maxLength: 20,
            },
          },
          {
            className: "col-12 col-sm-3",
            type: "input",
            key: "sdiCode",
            templateOptions: {
              label: this.translateService.instant("properties.fields.sdiCode"),
              placeholder: this.translateService.instant(
                "properties.fields.sdiCode"
              ),
              required: false,
              minLength: 6,
              maxLength: 7,
            },
          },
          {
            className: "col-12 col-sm-3",
            type: "day-month-picker",
            key: "administrationStartDate",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.administrationStartDate"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.administrationStartDate"
              ),
              minLength: 2,
              maxLength: 100,
            },
            modelOptions: {
              updateOn: "change",
            },
            // expressionProperties: {
            //   "templateOptions.datepickerOptions.max":
            //     "model.administrationEndDate",
            // },
          },
          {
            className: "col-12 col-sm-3",
            type: "day-month-picker",
            key: "administrationEndDate",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.administrationEndDate"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.administrationEndDate"
              ),
              minLength: 2,
              maxLength: 100,
            },
            modelOptions: {
              updateOn: "change",
            },
            // expressionProperties: {
            //   "templateOptions.datepickerOptions.min":
            //     "model.administrationStartDate",
            // },
          },
          // {
          //   className: "col-12 col-sm-6",
          //   type: "input",
          //   key: "iban",
          //   templateOptions: {
          //     label: this.translateService.instant("properties.fields.iban"),
          //     placeholder: this.translateService.instant(
          //       "properties.fields.iban"
          //     ),
          //     required: false,
          //     minLength: 27,
          //     maxLength: 27,
          //   },
          // },
          {
            className: "col-12 col-sm-3",
            type: "checkbox",
            key: "dossier",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant("properties.fields.dossier"),
              placeholder: this.translateService.instant(
                "properties.fields.dossier"
              ),
              required: false,
              disabled: false,
            },
          },
          {
            className: "col-12 col-sm-3",
            type: "input",
            key: "dossierEmail",
            templateOptions: {
              label: this.translateService.instant("properties.fields.dossierEmail"),
              placeholder: this.translateService.instant(
                "properties.fields.dossierEmail"
              ),
              required: false,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.model.dossier;
              },
              "templateOptions.required": (): boolean => {
                return this.model.dossier;
              },
            },
          },
          {
            className: "col-12 col-sm-6",
          },
          {
            className: "col-12 col-sm-3",
            type: "checkbox",
            key: "newDocumentTree",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant("properties.fields.newDocumentTree"),
              placeholder: this.translateService.instant(
                "properties.fields.newDocumentTree"
              ),
              required: false,
              disabled: false,
            },
          },
        ],
      },
    ];
    this.contractFields = [
      // ...this.contractFields,
      // 1. PLATFORM SUBSCRIPTION
      {
        fieldGroupClassName: "row",
        hooks: {
          onInit: (fieldConfig) => {
            this.updateSubscriptionCodes();
          },
        },
        fieldGroup: [
          {
            className: "col-12 boxForm",
            template:
              "<div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.subscription"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-4",
            type: "checkbox",
            key: "subscription.documentUpload",
            defaultValue: true,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.documentUpload.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.documentUpload.title"
              ),
            },
          },
          {
            className: "col-0 col-md-6",
          }, 
          {
            className: "col-12 col-md-4",
            type: "select",
            key: "subscription.invoiceHolder",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.invoiceHolder.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.invoiceHolder.title"
              ),
              required: true,
              multiple: false,
              options: [
                {
                  value: "property",
                  label: this.translateService.instant(
                    "companies.fields.subscription.invoiceHolder.property"
                  ),
                },
                {
                  value: "admin",
                  label: this.translateService.instant(
                    "companies.fields.subscription.invoiceHolder.admin"
                  ),
                },
                {
                  value: "owner",
                  label: this.translateService.instant(
                    "companies.fields.subscription.invoiceHolder.owner"
                  ),
                },
              ],
            },
            defaultValue:
              this.modelSC.subscription && this.modelSC.subscription.invoiceHolder
                ? this.modelSC.subscription.invoiceHolder
                : "property",
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.documentUpload",
              "templateOptions.disabled":
                "!model.subscription.documentUpload",
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-4",
            type: "select",
            key: "subscription.key",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.subscriptionType"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.subscriptionType"
              ),
              multiple: false,
              options: this.availableSubscriptionsKeys,
              required: true,
            },
            defaultValue:
              this.modelSC.subscription && this.modelSC.subscription.key
                ? this.modelSC.subscription.key
                : "FULL",
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.documentUpload",
              "templateOptions.disabled":
                "!model.subscription.documentUpload",
            },
          },

          {
            className: "col-4",
            type: "input",
            key: "subscription.fee",
            defaultValue: this.defaultFee,
            templateOptions: {
              label: this.translateService.instant("subscriptions.fields.fee"),
              placeholder: this.translateService.instant(
                "subscriptions.fields.fee"
              ),
              type: "number",
              step: 0.1,
              min: 0,
              max: 10000,
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.documentUpload && model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled":
                "!model.subscription.documentUpload || (model.subscription.key && (model.subscription.key === 'noSubscription' || model.subscription.key === 'PRIVACY'))",
            },
          },
          {
            className: "col-0 col-md-4",
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "subscription.availableSpace",
            defaultValue: this.defaultAvailableSpace,
            templateOptions: {
              label: this.translateService.instant(
                "subscriptions.fields.availableSpace"
              ),
              placeholder: this.translateService.instant(
                "subscriptions.fields.availableSpace"
              ),
              type: "number",
              min: 0,
              max: 1000,
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled":
                "!model.subscription.documentUpload || (model.subscription.key && (model.subscription.key === 'noSubscription' || model.subscription.key === 'PRIVACY'))",
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "subscription.totalStorageSpaceUsed",
            templateOptions: {
              label: this.translateService.instant(
                "subscriptions.fields.totalStorageSpaceUsed"
              ),
              placeholder: this.translateService.instant(
                "subscriptions.fields.totalStorageSpaceUsed"
              ),
              type: "number",
              step: 0.001,
              min: 0,
              max: 50000,
              disabled: true,
            },
            defaultValue: 0,
          },
          {
            className: "col-0 col-md-4",
          },
        ],
      },
      // 2. STRUCTURE SUBSCRIPTION
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.structure.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.structure",
            defaultValue: true,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.structure.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.structure.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY' && model.subscription.key !== 'FREE' && model.subscription.key !== 'FREEBASIC'",
              "templateOptions.disabled":
                "!model.subscription.documentUpload || (model.subscription.key && (model.subscription.key === 'noSubscription' || model.subscription.key === 'PRIVACY' || model.subscription.key === 'FREE' || model.subscription.key === 'FREEBASIC'))",
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.structurePrice",
            defaultValue: this.defaultStructurePrice,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.structurePrice"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.structurePrice"
              ),
              type: "number",
              step: 0.1,
              min: 0,
              max: 10000,
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.structure",
              "templateOptions.disabled":
                "!model.subscription.documentUpload || (model.subscription.key && (model.subscription.key === 'noSubscription' || model.subscription.key === 'PRIVACY' || model.subscription.key === 'FREE' || model.subscription.key === 'FREEBASIC' || !model.subscription.structure))",
            },
          },
        ],
      },
      // 3. FIRST BALANCE
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.firstBalance.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.firstPriceList",
            defaultValue: true,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.firstPriceList.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.firstPriceList.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled":
                "!model.subscription.documentUpload || (model.subscription.key && (model.subscription.key === 'noSubscription' || model.subscription.key === 'PRIVACY'))",
            },
          },
          {
            className: "col-1",
          },
        ],
      },
      // 4. SUBSEQUENT INSERTIONS
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.subsequentInsertions.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.scanInsNext",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.scanInsNext.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.scanInsNext.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled":
                "!model.subscription.documentUpload || (model.subscription.key && (model.subscription.key === 'noSubscription' || model.subscription.key === 'PRIVACY' || model.subscription.flatRate))",
            },
          },
          {
            className: "col-10",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.flatRate",
            defaultValue: true,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled":
                "!model.subscription.documentUpload || (model.subscription.key && (model.subscription.key === 'noSubscription' || model.subscription.key === 'PRIVACY' || model.subscription.scanInsNext))",
            },
          },
          {
            className: "col-2",
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalDoc",
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalDoc"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalDoc"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalPdf",
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalPdf"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalPdf"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
          },
        ],
      },
      // 5. SUBSEQUENT SCANS
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.subsequentScans.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.scanNext",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.scanNext.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.scanNext.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled":
                "!model.subscription.documentUpload || (model.subscription.key && (model.subscription.key === 'noSubscription' || model.subscription.key === 'PRIVACY' || model.subscription.flatRateScan))",
            },
          },
          {
            className: "col-10",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.flatRateScan",
            defaultValue: true,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRateScan.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRateScan.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled":
                "!model.subscription.documentUpload || (model.subscription.key && (model.subscription.key === 'noSubscription' || model.subscription.key === 'PRIVACY' || model.subscription.scanNext))",
            },
          },
          {
            className: "col-10",
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalA4",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA4"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA4"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
            defaultValue: 0,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalA3",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA3"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA3"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
            defaultValue: 0,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalA2",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA2"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA2"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
            defaultValue: 0,
          },
          {
            className: "col-1",
          },
          {
            className: "col-2",
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalA1",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA1"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA1"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
            defaultValue: 0,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalA0",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA0"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA0"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
            defaultValue: 0,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalOutFormat",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalOutFormat"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalOutFormat"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
            defaultValue: 0,
          },
          {
            className: "col-1",
          },
        ],
      },
      // 6. SUBSCRIPTION OPTIONS
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.optionsNfc.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-4",
            type: "checkbox",
            key: "subscription.nfcAccess",
            defaultValue: true,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.nfcAccess"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.nfcAccess"
              ),
              required: false,
            },
          },
          {
            className: "col-12 col-md-2",
            type: "input",
            key: "subscription.nfcAccessFee",
            defaultValue: this.defaultNfcAccessFee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.nfcAccessFee"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.nfcAccessFee"
              ),
              type: "number",
              step: 0.1,
              min: 0,
              max: 10000,
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.nfcAccess",
              "templateOptions.disabled": "!model.subscription.nfcAccess",
            },
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.nfcAccessBlocked",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.nfcAccessBlocked"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.nfcAccessBlocked"
              ),
              required: false,
            },
          },
          {
            className: "col-12 col-md-2",
            type: "input",
            key: "subscription.nfcAccessTagFee",
            defaultValue: this.defaultNfcAccessFee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.nfcAccessTagFee"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.nfcAccessTagFee"
              ),
              type: "number",
              step: 0.1,
              min: 0,
              max: 10000,
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.nfcAccess",
              "templateOptions.disabled": "!model.subscription.nfcAccess",
            },
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.nfcAccessTagBlocked",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.nfcAccessTagBlocked"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.nfcAccessTagBlocked"
              ),
              required: false,
            },
          },
          {
            className: "col-12 col-md-4",
            type: "datepicker",
            key: "subscription.startDateNfcAccess",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.startDateNfcAccess"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.startDateNfcAccess"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.nfcAccess",
              "templateOptions.disabled":
                "!model.subscription.nfcAccess",
            },
          },
          {
            className: "col-12 col-md-4",
            type: "datepicker",
            key: "subscription.endDateNfcAccess",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.endDateNfcAccess"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.endDateNfcAccess"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.nfcAccess",
              "templateOptions.disabled":
                "!model.subscription.nfcAccess",
            },
          },
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.optionsSupplierEvaluation.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-4",
            type: "checkbox",
            key: "subscription.suppliers",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.suppliers"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.suppliers"
              ),
              required: false,
            },
          },
          {
            className: "col-12 col-md-2",
            type: "input",
            key: "subscription.suppliersFee",
            defaultValue: this.defaultSuppliersFee,
            templateOptions: {
              label: this.translateService.instant("subscriptions.fields.fee"),
              placeholder: this.translateService.instant(
                "subscriptions.fields.fee"
              ),
              type: "number",
              step: 0.1,
              min: 0,
              max: 10000,
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.suppliers",
              "templateOptions.disabled": "!model.subscription.suppliers"
            },
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.suppliersBlocked",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.suppliersBlocked"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.suppliersBlocked"
              ),
              required: false,
            },
          },
          {
            className: "col-md-4",
          },
          {
            className: "col-12 col-md-4",
            type: "datepicker",
            key: "subscription.startDateSuppliers",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.startDateSuppliers"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.startDateSuppliers"
              ),
              disabled: true,
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.suppliers",
            },
          },
          {
            className: "col-12 col-md-4",
            type: "datepicker",
            key: "subscription.endDateSuppliers",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.endDateSuppliers"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.endDateSuppliers"
              ),
              disabled: true,
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.suppliers",
            },
          },
          {
            className: "col-md-8",
          },
          {
            className: "col-md-4",
          },
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.optionsPrivacy.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.privacy",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.privacy"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.privacy"
              ),
              required: false,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  this.modelSC.subscription.privacySag
                );
              },
            },
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.privacySag",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.privacySag"
              ),
              required: false,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  this.modelSC.subscription.privacy
                );
              },
            },
          },
          {
            className: "col-12 col-md-2",
            type: "input",
            key: "subscription.privacyFee",
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant("subscriptions.fields.fee"),
              placeholder: this.translateService.instant(
                "subscriptions.fields.fee"
              ),
              type: "number",
              step: 0.1,
              min: 0,
              max: 10000,
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.privacy || model.subscription.privacySag",
            },
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.privacyBlocked",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.privacyBlocked"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.privacyBlocked"
              ),
              required: false,
            },
          },
          {
            className: "col-md-4",
          },
          {
            className: "col-12 col-md-4",
            type: "datepicker",
            key: "subscription.startDatePrivacy",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.startDatePrivacy"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.startDatePrivacy"
              ),
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.privacy || model.subscription.privacySag",
            },
          },
          {
            className: "col-12 col-md-4",
            type: "datepicker",
            key: "subscription.endDatePrivacy",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.endDatePrivacy"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.endDatePrivacy"
              ),
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.privacy || model.subscription.privacySag",
            },
          },
        ],
      },
      // 7. PRICE LIST
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.priceList.title"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
            defaultValue: this.prices[index].key,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            defaultValue: this.prices[0].fee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
            defaultValue: this.prices[index].key,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            defaultValue: this.prices[0].fee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
            defaultValue: this.prices[index].key,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            defaultValue: this.prices[0].fee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
            defaultValue: this.prices[index].key,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            defaultValue: this.prices[0].fee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12",
            template: "<hr />",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
            defaultValue: this.prices[index].key,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            defaultValue: this.prices[0].fee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
            defaultValue: this.prices[index].key,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            defaultValue: this.prices[0].fee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
            defaultValue: this.prices[index].key,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            defaultValue: this.prices[0].fee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
            defaultValue: this.prices[index].key,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            defaultValue: this.prices[0].fee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
            defaultValue: this.prices[index].key,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            defaultValue: this.prices[0].fee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
            defaultValue: this.prices[index].key,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            defaultValue: this.prices[0].fee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12",
            template: "<hr />",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
            defaultValue: this.prices[index].key,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            defaultValue: this.prices[0].fee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12",
            template: "<hr />",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                "companies.fields.subscription.additionalStorage.sectionTitle"
              ) +
              "</div>",
          },
          {
            className: "col-3",
            type: "input",
            key: "subscription.extraSpaceFee",
            defaultValue: this.defaultExtraSpaceFee,
            templateOptions: {
              label: this.translateService.instant(
                "subscriptions.fields.extraSpaceFee"
              ),
              placeholder: this.translateService.instant(
                "subscriptions.fields.extraSpaceFee"
              ),
              type: "number",
              min: 0,
              max: 500,
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled":
                "model.subscription.key && (model.subscription.key === 'noSubscription' || model.subscription.key === 'PRIVACY')",
            },
          },
        ],
      },
      // // Notes
      // {
      //   fieldGroupClassName: "row",
      //   fieldGroup: [
      //     {
      //       className: "col-12",
      //       template:
      //         "<hr /><div><strong>" +
      //         this.translateService.instant(
      //           "companies.fields.subscription.notes"
      //         ) +
      //         "</strong></div>",
      //     },
      //     {
      //       className: "col-12",
      //       type: "textarea",
      //       key: "subscription.notes",
      //       templateOptions: {
      //         rows: 5,
      //       },
      //     },
      //   ],
      // },
    ];
  }

  /**
   * Autofill subscription values based on subscription type changes
   *
   * @since 1.0.0
   */
  updateSubscriptionCodes(): void {
    const subscriptionControl = this.formSC.controls.subscription;

    // Control fields
    const keyControl = this.formSC.controls.subscription.get("key");
    const suppliersControl = this.formSC.controls.subscription.get(
      "suppliers"
    );
    const startDateNfcAccessControl = this.formSC.controls.subscription.get(
      "startDateNfcAccess"
    );
    const startDateSuppliersControl = this.formSC.controls.subscription.get("startDateSuppliers");

    // Value fields
    const feeField = subscriptionControl.get("fee");
    const availableSpaceField = subscriptionControl.get("availableSpace");
    const structureField = subscriptionControl.get("structure");
    const structurePriceField = subscriptionControl.get("structurePrice");
    const firstPriceListField = subscriptionControl.get("firstPriceList");
  
    const scanInsNextField = subscriptionControl.get("scanInsNext");
    const flatRateField = subscriptionControl.get("flatRate");
    const scanNextField = subscriptionControl.get("scanNext");
    const flatRateScanField = subscriptionControl.get("flatRateScan");
    const startDateSuppliersField = subscriptionControl.get("startDateSuppliers");
    const endDateSuppliersField = subscriptionControl.get("endDateSuppliers");
    const startDateNfcAccessField = subscriptionControl.get(
      "startDateNfcAccess"
    );
    const endDateNfcAccessField = subscriptionControl.get("endDateNfcAccess");
    const extraSpaceFeeField = subscriptionControl.get("extraSpaceFee");

    const startDatePrivacyControl =
      this.formSC.controls.subscription.get("startDatePrivacy");
    const privacyControl = this.formSC.controls.subscription.get(
      "privacy"
    );
    const privacySagControl = this.formSC.controls.subscription.get(
      "privacySag"
    );
    const startDatePrivacyField = subscriptionControl.get("startDatePrivacy");
    const endDatePrivacyField = subscriptionControl.get("endDatePrivacy");

    // Option fields
    let codeField: FormlyFieldConfig;
    if (this.contractFields && Array.isArray(this.contractFields)) {
      this.contractFields.forEach((el: any) => {
        if (el.fieldGroup && Array.isArray(el.fieldGroup)) {
          el.fieldGroup.forEach((subEl: any) => {
            switch (subEl.key) {
              case "subscription.code":
                codeField = subEl;
                break;
            }
          });
        }
      });
    }

    if (keyControl) {
      keyControl.valueChanges
        .pipe(distinctUntilChanged(), startWith(keyControl.value))
        .subscribe(
          (res) => {
            if (!res) {
              return;
            }

            let index = -1;
            switch (res) {
              case "noSubscription":
              case "PRIVACY":
                feeField.setValue(0);
                availableSpaceField.setValue(0);
                extraSpaceFeeField.setValue(0);
                structurePriceField.setValue(0);
                structureField.setValue(false);
                firstPriceListField.setValue(false);
                scanInsNextField.setValue(false);
                flatRateField.setValue(false);
                scanNextField.setValue(false);
                flatRateScanField.setValue(false);
                
                break;
              case "FULL":
                index = this.archediSubscriptions.findIndex((el: any) => {
                  return el.key === "FULL";
                });
                structureField.setValue(true);
                firstPriceListField.setValue(true);
                scanInsNextField.setValue(false);
                flatRateField.setValue(true);
                scanNextField.setValue(true);
                flatRateScanField.setValue(false);
                break;
              case "FULLBASIC":
                index = this.archediSubscriptions.findIndex((el: any) => {
                  return el.key === "FULLBASIC";
                });
                structureField.setValue(true);
                firstPriceListField.setValue(true);
                scanInsNextField.setValue(true);
                flatRateField.setValue(false);
                scanNextField.setValue(true);
                flatRateScanField.setValue(false);
                break;
              case "FREE":
                index = this.archediSubscriptions.findIndex((el: any) => {
                  return el.key === "FREE";
                });
                structureField.setValue(false);
                firstPriceListField.setValue(true);
                scanInsNextField.setValue(false);
                flatRateField.setValue(true);
                scanNextField.setValue(true);
                flatRateScanField.setValue(false);
                break;
              case "FREEBASIC":
                index = this.archediSubscriptions.findIndex((el: any) => {
                  return el.key === "FREEBASIC";
                });
                structureField.setValue(false);
                firstPriceListField.setValue(true);
                scanInsNextField.setValue(true);
                flatRateField.setValue(false);
                scanNextField.setValue(true);
                flatRateScanField.setValue(false);
                break;
              }
            if (index > -1) {
              feeField.setValue(this.archediSubscriptions[index].fee);
              availableSpaceField.setValue(
                this.archediSubscriptions[index].availableSpace
              );
              extraSpaceFeeField.setValue(
                this.archediSubscriptions[index].extraSpaceFee
              );
              structurePriceField.setValue(
                this.archediSubscriptions[index].structurePrice
              );
            }

            // Backend requires a billing code whenever the matching flag is enabled.
            // Only one code is currently defined per list, so it is used directly
            // rather than filtered by plan (their "keys" arrays are inconsistently
            // maintained and do not cover every plan that sets these flags, e.g. FULLBASIC).
            const scanInsNextCode = this.configService.availableInsCodesNext[0];
            const flatRateCode = this.configService.availableInsCodesNextForfait[0];
            const scanNextCode = this.configService.availableScanCodesNext[0];
            const flatRateScanCode = this.configService.availableScanCodesNextForfait[0];
            this.modelSC.subscription = this.modelSC.subscription || {};
            this.modelSC.subscription.scanInsNextCode =
              scanInsNextField && scanInsNextField.value && scanInsNextCode
                ? scanInsNextCode.value
                : "INSSUCC001";
            this.modelSC.subscription.flatRateCode =
              flatRateField && flatRateField.value && flatRateCode
                ? flatRateCode.value
                : "FINSSUCC001";
            this.modelSC.subscription.scanNextCode =
              scanNextField && scanNextField.value && scanNextCode
                ? scanNextCode.value
                : "SCASUCC001";
            this.modelSC.subscription.flatRateScanCode =
              flatRateScanField && flatRateScanField.value && flatRateScanCode
                ? flatRateScanCode.value
                : "FSCASUCC001";
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }

    if (suppliersControl) {
      suppliersControl.valueChanges
        .pipe(distinctUntilChanged(), startWith(suppliersControl.value))
        .subscribe(
          (res) => {
            if (!res) {
              startDateSuppliersField.setValue(null);
              endDateSuppliersField.setValue(null);
            } else {
              startDateSuppliersField.setValue(this.masterStarDateSupplier);
              endDateSuppliersField.setValue(this.masterEndDateSupplier);
            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }

    if (startDateNfcAccessControl) {
      startDateNfcAccessControl.valueChanges
        .pipe(distinctUntilChanged(), startWith(startDateNfcAccessControl.value))
        .subscribe(
          (res) => {
            if (!res) {
              return;
            }
            if (res && startDateNfcAccessField.dirty) {
              // Set endDate automatically to startDate + 1 year
              const start = new Date(res);
              const end = start;
              end.setDate(start.getDate() - 1);
              end.setFullYear(end.getFullYear() + 1);
              endDateNfcAccessField.setValue(end);
            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }

    if (startDateSuppliersControl) {
      startDateSuppliersControl.valueChanges
      .pipe(distinctUntilChanged(), startWith(startDateSuppliersControl.value))
      .subscribe(
        (res) => {
          if (!res) {
            return;
          }
          if (res && startDateSuppliersField.dirty) {
            // Set endDate automatically to startDate + 1 year
            const start = new Date(res);
            const end = start;
            end.setDate(start.getDate() - 1);
            end.setFullYear(end.getFullYear() + 1);
            endDateSuppliersField.setValue(end);
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
    }

    if (startDatePrivacyControl) {
      startDatePrivacyControl.valueChanges
        .pipe(distinctUntilChanged(), startWith(startDatePrivacyControl.value))
        .subscribe(
          (res) => {
            if (!res) {
              return;
            }
            if (res && startDatePrivacyField !== null && startDatePrivacyField.dirty && endDatePrivacyField !== null) {
              // Set endDate automatically to startDate + 1 year
              const start = new Date(res);
              const end = start;
              end.setDate(start.getDate() - 1);
              end.setFullYear(end.getFullYear() + 1);
              endDatePrivacyField.setValue(end);
            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }

  }
  
  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(formNumber: number): void {
    // Prevent invalid form submit
    if (formNumber === 1) {
      if (this.form.invalid) {
        Object.keys(this.form.controls).forEach((key) => {
          this.form.controls[key].markAsTouched();
        });
        this.toastrService.error(
          this.translateService.instant("common.errors.form_is_invalid")
        );
        return;
      }
    }
    if (formNumber === 2) {
      if (this.form.invalid || this.formSC.invalid) {
        Object.keys(this.form.controls).forEach((key) => {
          this.form.controls[key].markAsTouched();
        });
        Object.keys(this.formSC.controls).forEach((key) => {
          this.formSC.controls[key].markAsTouched();
        });
        this.toastrService.error(
          this.translateService.instant("common.errors.form_is_invalid")
        );
        console.warn(this.form);
        console.warn(this.formSC);
        return;
      }
    }

    // We don't want to send and empty iban
    if (this.model.iban === "") {
      delete this.model.iban;
    }

    let subscription: any;
    if (formNumber === 1) {
      subscription = this.modelSC.subscription ? this.modelSC.subscription : undefined;
      if (!subscription) {
        this.toastrService.error(
          this.translateService.instant("common.errors.form_is_invalid")
        );
        return;
      }
    }
    if (formNumber === 2) {
      subscription = this.modelSC.subscription ? this.modelSC.subscription : undefined;
      if (!subscription) {
        this.toastrService.error(
          this.translateService.instant("common.errors.form_is_invalid")
        );
        return;
      }
    }
    // Activate submit button loading status
    this.formBtnOptions.active = true;

    // START - Create balance details on subscriptions
    let description = "";

    const detail = [];
    if (subscription.documentUpload) {
      detail.push({
        code: subscription.key,
        description,
        quantity: 1,
        unitPrice: subscription.fee,
        totalRow: subscription.fee,
      });
    }
    // structure
    if (subscription.structure  && subscription.key !== "FREE"  && subscription.key !== "FREEBASIC"){ 
      description = "";
      detail.push({
        code: "", 
        description,
        quantity: 1,
        unitPrice: subscription.structurePrice,
        totalRow: subscription.structurePrice,
      });
    }

    if (subscription.nfcAccess) {
      const sub = this.configService.availableOptionsCodes.filter((el: any) => {
        return el.value === "NFC001";
      })[0];
      description = "";
      description =
        sub && sub.description ? (sub.description as string) : "";
        description = description.replace(/\s\s+/g, " ");
      detail.push({
        code: "NFC001",
        description,
        quantity: 1,
        unitPrice: subscription.nfcAccessFee,
        totalRow: subscription.nfcAccessFee,
      });
    }
    if (subscription.suppliers) {
      const sub = this.configService.availableOptionsCodes.filter((el: any) => {
        return el.value === "SPL001";
      })[0];
      description = "";
      description =
        sub && sub.description ? (sub.description as string) : "";
        description = description.replace(/\s\s+/g, " ");
        description = description.replace(
          "#year#",
          moment(subscription.startDateSuppliers).year().toString()
        );
      detail.push({
        code: "SPL001",
        description,
        quantity: 1,
        unitPrice: subscription.suppliersFee,
        totalRow: subscription.suppliersFee,
      });
    }

    const newSubscription = { ...subscription, detail };
    this.model = { ...this.model, subscription: newSubscription };
    // END - Create balance details on subscriptions

    this.subscriptions.push(
      this.propertiesService
        .create(this.model)
        .pipe(finalize(() => (this.formBtnOptions.active = false)))
        .subscribe(
          (res) => {
            if (res.status) {
              this.toastrService.success(res.message);
              setTimeout(() => {
                const lang = this.translateService.currentLang;
                this.router.navigate([
                  lang,
                  "app",
                  "properties",
                  res.data._id,
                  "home",
                ]);
              }, this.configService.redirectDelayAfterCreation);
              return;
            }
          },
          (err) => {
            // Choose one of the following error handling method.
            // 1) First one show a message right under the form fields
            //    (if the form is properly setted)
            // 2) Second one is used to show a mat-error under each field
            //    in the dynamic form (is meant to be used only in dynamicForm)
            // 3) Third one show toastr notifications for each error
            // 4) Fourth one is used to show mat-error under each field
            //    in the ngx-formly form (is mean to be used only with ngx-formly)
            // this.helperService.handleFormError(form, err);
            // this.helperService.handleDynamicFormError(form, err);
            // this.helperService.handleError(err);
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
    
  }

  // Copy subscription data from admin company
  onAdminCompanySelected(company?: ICompany): void {
    if (!this.model.admin || !company) {
      return;
    }

    if (company && company.subscription) {
      this.companyExists = true;
      if (company.email && company.email !== "") {
        const notifyMail1Control = this.form.controls.notifyMail1;
        notifyMail1Control.setValue(company.email);
      } else {
        const notifyMail1Control = this.form.controls.notifyMail1;
        notifyMail1Control.setValue("");
      }

      // Before set company condictions, reset all form data
      const subscriptionControlSC = this.formSC.controls.subscription;
      const keyFieldSC = subscriptionControlSC.get("key");
      keyFieldSC.setValue('noSubscription');

      if (subscriptionControlSC) {
        Object.keys(company.subscription).forEach((key) => {
          const controlSC = subscriptionControlSC.get(key);
          if (
            key &&
            key !== "notes" &&
            // key !== "startDateNfcAccess" &&
            // key !== "endDateNfcAccess" &&
            controlSC
          ) {
            if (key === 'startDateSuppliers') {
              this.masterStarDateSupplier = company.subscription[key];
            }
            if (key === 'endDateSuppliers') {
              this.masterEndDateSupplier = company.subscription[key];
            }
            controlSC.setValue(company.subscription[key]);
          }
        });
      }
    }
  }
}
